/**
 * useHomeTour
 * Tour for the home page (first-time visitors)
 */

import { Tour } from '@shared/lib/tours/types'
import { TOUR_THEME } from '@shared/lib/tours/theme'
import type { Config } from 'driver.js'
import { useEffect } from 'react'
import { usePageReadyForTour } from './usePageReadyForTour'
import { useTrackedDriverTour } from './useTrackedDriverTour'
import { markTourAsCompleted, useTourManager } from './useTourManager'

const TOUR_NAME = 'home'

function homeTourConfig(): Config {
  return {
    ...TOUR_THEME,
    showProgress: true,
    nextBtnText: 'Siguiente',
    prevBtnText: 'Anterior',
    doneBtnText: '¡Listo!',
    progressText: '{{current}} de {{total}}',

    onDestroyed: () => {
      markTourAsCompleted(TOUR_NAME)
    },

    steps: [
      {
        popover: {
          title: '¡Bienvenido a ProFessionals!',
          description:
            'Te mostraremos cómo encontrar el profesional perfecto para tu proyecto. Este tour solo tomará un momento.',
        },
      },
      {
        element: '[data-tour="search-bar"]',
        popover: {
          title: 'Busca Rápidamente',
          description:
            'Usá la barra de búsqueda para encontrar profesiones específicas. Los resultados se actualizan en tiempo real.',
          side: 'bottom',
        },
      },
      {
        element: '[data-tour="professions-grid"]',
        popover: {
          title: 'Explora Profesiones',
          description:
            'Navegá por las categorías de profesionales disponibles. Hacé clic en cualquier profesión para ver los expertos.',
          side: 'bottom',
        },
      },
      {
        element: '[data-tour="navbar"]',
        popover: {
          title: 'Navegación',
          description: 'Usá estos botones para navegar por la app.',
          side: 'top',
        },
      },
      {
        element: '[data-tour="navbar-profile"]',
        popover: {
          title: 'Tu Cuenta',
          description:
            'Desde acá podés acceder a tu perfil, gestionar tus datos y activar tu perfil profesional si ofrecés servicios.',
          side: 'top',
        },
      },
    ],
  }
}

export function useHomeTour(autoStart: boolean = false): Tour {
  const { isTourCompleted, shouldShowTour, resetTour } = useTourManager()
  const isPageReady = usePageReadyForTour()
  const startTrackedTour = useTrackedDriverTour()

  const startTour = () => {
    startTrackedTour(homeTourConfig())
  }

  // Auto-start tour on mount if conditions are met
  useEffect(() => {
    // Only start tour when page is ready (loaded and no PWA prompt)
    if (!isPageReady) return

    if (autoStart && shouldShowTour(TOUR_NAME)) {
      return startTrackedTour(homeTourConfig(), { delayMs: 300 })
    }
  }, [autoStart, isPageReady, shouldShowTour, startTrackedTour])

  const reset = () => {
    resetTour(TOUR_NAME)
  }

  return {
    startTour,
    isCompleted: isTourCompleted(TOUR_NAME),
    reset,
  }
}
